export var SortOrder;
(function (SortOrder) {
    SortOrder[SortOrder["desc"] = -1] = "desc";
    SortOrder[SortOrder["asc"] = 1] = "asc";
})(SortOrder || (SortOrder = {}));
const NATURAL_SORT_OPTIONS = {
    numeric: true,
    ignorePunctuation: true,
    sensitivity: "base",
};
/**
 * This function will sort an array of object based on the given property, using `localCompare`
 *
 * This function accepts two additional parameters `fallbackProperty` and `secondFallbackProperty`
 * in order to perform the sort, in case `property` doesn't exist in the object.
 *
 * However, please note that if _non of the given properties_ exist, this function will skip the
 * sort and assume that the current order is correct (i.e. return 0).
 *
 * TODO: this function has a few idiosyncracies (see comments in function), so you should probably
 * not use it. We keep it here because it's needed in order to sort terminals in a
 * historically-stable order.
 *
 * @param property - the primary property to sort
 * @param fallbackProperty - if primary property doesn't exist, sort by `fallbackProperty`
 * @param secondFallbackProperty - if `fallbackProperty` doesn't exist, sort by this `secondFallbackProperty`
 * @param {SortOrder} sortOrder - eiter SortOrder.asc OR SortOrder.desc to indicate the sorting order
 */
export function naturalSortByPropertyWithFallbacks(property, fallbackProperty, secondFallbackProperty, opts = {}) {
    // Use "en" locale when deterministic is true or when navigator is not available (Node.js environment)
    const hasNavigator = typeof navigator !== "undefined";
    const locale = opts.deterministic || !hasNavigator ? "en" : navigator.languages[0] ?? navigator.language;
    return (a, b) => {
        let aProperty = a[property];
        let bProperty = b[property];
        if (fallbackProperty && !secondFallbackProperty) {
            // Note: although a tighter definition for this function might use `??` instead of `||`
            // here, or even `property in a`, so that we fallback only for nonexistent values, and
            // not for empty strings, don't just change this behaviour. The only caller of this
            // function (sortTerminals) depends on the empty string fallback in order to stably
            // sort terminals with an unset Terminal Order property (which is a default property,
            // so will commonly be set to empty string when unset, which would result in unstable)
            aProperty = aProperty || a[fallbackProperty];
            bProperty = bProperty || b[fallbackProperty];
        }
        else if (fallbackProperty && secondFallbackProperty) {
            aProperty = aProperty || a[fallbackProperty] || a[secondFallbackProperty];
            bProperty = bProperty || b[fallbackProperty] || b[secondFallbackProperty];
        }
        if (aProperty && bProperty) {
            // aProperty is not guaranteed to be a string. If it's not, localeCompare(..) will not be
            // defined. aProperty could be a number. Serializing ensures localeCompare(..) can be called
            const aPropertyString = typeof aProperty === "string" ? aProperty : `${aProperty}`;
            // In case property is a sentence... we need to strip off all the internal spaces
            const result = aPropertyString.localeCompare(bProperty, locale, NATURAL_SORT_OPTIONS);
            return result * (opts.order ?? SortOrder.asc);
        }
        // If anything wrong with `aProperty` OR `bProperty`, return the original order
        return 0;
    };
}
export function naturalSortByProperty(property, opts = {}) {
    // Use "en" locale when deterministic is true or when navigator is not available (Node.js environment)
    const hasNavigator = typeof navigator !== "undefined";
    const locale = opts.deterministic || !hasNavigator ? "en" : navigator.languages[0] ?? navigator.language;
    return (a, b) => {
        const result = (a[property] ?? "").localeCompare(b[property] ?? "", locale, NATURAL_SORT_OPTIONS);
        return result * (opts.order ?? SortOrder.asc);
    };
}
export function sortByProperty(property, opts = {}) {
    return (a, b) => {
        const result = a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0;
        return result * (opts.order ?? SortOrder.asc);
    };
}
export function naturalSort(opts = {}) {
    // Use "en" locale when deterministic is true or when navigator is not available (Node.js environment)
    const hasNavigator = typeof navigator !== "undefined";
    const locale = opts.deterministic || !hasNavigator ? "en" : navigator.languages[0] ?? navigator.language;
    return (a, b) => {
        const result = a.localeCompare(b, locale, NATURAL_SORT_OPTIONS);
        return result * (opts.order ?? SortOrder.asc);
    };
}
/** Given a function that returns a derived value from an object, return a sorting
 * function that will sort the list by the derived value. Similar to python's key=
 * parameter.
 */
export function keySort(makeKey) {
    // Don't use the `(a, b) => a - b` shorthand here, because key values might not be numeric.
    return (a, b) => (makeKey(a) > makeKey(b) ? 1 : makeKey(a) < makeKey(b) ? -1 : 0);
}
//# sourceMappingURL=sort.js.map